import { useEffect, useRef } from 'react';
import { pick, prop } from 'ramda';
import { usePrevious } from '@core/js/hooks/usePrevious';
import { useUserSelector, useCommunicationWidgetsSelector } from '@core/js/store/hooks';
import { useRemoveQueryParams } from '@theme/js/hooks/navigate';
import * as urlUtils from '@theme/js/utils/url.util';

import * as hooks from './hooks';

export const useInitWidgets = () => {
    const removeQueryParams = useRemoveQueryParams(); /* Lugano changes */
    const chatSearchParam = urlUtils.getLocationParam('openchat'); /* Lugano changes */
    const { lang, isLoggedIn } = useUserSelector(pick(['lang', 'isLoggedIn']));
    const prevLang = usePrevious(lang);
    const init = hooks.useInit();
    const login = hooks.useLogin(chatSearchParam); /* Lugano changes */
    const logout = hooks.useLogout();
    const isLoaded = hooks.useLoadWidgets();
    const setTranslations = hooks.useSetTranslations();
    const isConnected = hooks.useSetConnection(isLoaded);
    const { enableChat, enableInbox } = hooks.useIsWidgetsEnabled();
    const wasLoggedIn = usePrevious(isLoggedIn);
    const isWidgetsAuthenticated = useCommunicationWidgetsSelector(prop('isWidgetsAuthenticated'));
    const firstInitRef = useRef(true); /* Lugano changes */

    useEffect(() => {
        if (isConnected) {
            /* Lugano changes */
            if (firstInitRef.current && isLoggedIn && isWidgetsAuthenticated) {
                enableChat && init('chat');
                firstInitRef.current = false;
            }
            /* Lugano changes */
            enableInbox && init('inbox');
        }
    }, [isConnected, init, enableChat, enableInbox, isLoggedIn, isWidgetsAuthenticated]);

    useEffect(() => {
        if (isConnected || prevLang !== lang) {
            enableChat && setTranslations('chat');
            enableInbox && setTranslations('inbox');
        }
    }, [lang, isConnected, setTranslations, enableChat, enableInbox, prevLang]);

    useEffect(() => {
        if (!isConnected) {
            return;
        }

        if (isLoggedIn && !isWidgetsAuthenticated) {
            login();
        }
    }, [isConnected, isLoggedIn, login, isWidgetsAuthenticated]);

    /* Lugano changes */
    useEffect(() => {
        if (!isConnected) {
            return;
        }

        if (isLoggedIn && isWidgetsAuthenticated && wasLoggedIn) {
            if (chatSearchParam === 'true') {
                setTimeout(() => {
                    enableChat && window.Communication.API.chat.maximize();
                    removeQueryParams(['openchat']); /* Lugano changes */
                }, 3000);
            }
        }
    }, [
        isConnected, isLoggedIn, isWidgetsAuthenticated,
        chatSearchParam, enableChat,
        wasLoggedIn, removeQueryParams
    ]);
    /* Lugano changes */

    useEffect(() => {
        if (!isConnected) {
            return;
        }

        if (!isLoggedIn && wasLoggedIn) {
            logout();
        }
    }, [isConnected, wasLoggedIn, isLoggedIn, logout]);
};
